export interface SessionTokens {
  accessToken: string;
  refreshToken: string;
}

export type AuthPersistenceMode = "persistent" | "session";

export interface AuthSessionSnapshot {
  accessToken: string | null;
  refreshToken: string | null;
  persistence: AuthPersistenceMode | null;
}

export interface SetSessionTokensOptions {
  persistence?: AuthPersistenceMode;
}

export type AuthStoreListener = (snapshot: AuthSessionSnapshot) => void;

export interface AuthStore {
  getSnapshot(): AuthSessionSnapshot;
  getAccessToken(): string | null;
  getRefreshToken(): string | null;
  getPersistenceMode(): AuthPersistenceMode | null;
  hasSession(): boolean;
  setTokens(tokens: SessionTokens, options?: SetSessionTokensOptions): AuthSessionSnapshot;
  clear(): AuthSessionSnapshot;
  subscribe(listener: AuthStoreListener): () => void;
}

const AUTH_STORAGE_KEY = "msg.auth.tokens.v1";

interface PersistedAuthTokens {
  accessToken: string;
  refreshToken: string;
}

function normalizeToken(value: string): string | null {
  const normalized = value.trim();
  if (normalized === "") {
    return null;
  }
  return normalized;
}

function cloneSnapshot(snapshot: AuthSessionSnapshot): AuthSessionSnapshot {
  return {
    accessToken: snapshot.accessToken,
    refreshToken: snapshot.refreshToken,
    persistence: snapshot.persistence,
  };
}

function isPersistenceMode(value: unknown): value is AuthPersistenceMode {
  return value === "persistent" || value === "session";
}

function readStorage(mode: AuthPersistenceMode): Storage | null {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    return mode === "persistent" ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

function readPersistedSnapshot(mode: AuthPersistenceMode): AuthSessionSnapshot | null {
  const storage = readStorage(mode);
  if (storage === null) {
    return null;
  }

  let raw: string | null = null;
  try {
    raw = storage.getItem(AUTH_STORAGE_KEY);
  } catch {
    return null;
  }

  if (raw === null || raw.trim() === "") {
    return null;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return null;
  }

  const persisted = parsed as Partial<PersistedAuthTokens>;
  if (typeof persisted.accessToken !== "string" || typeof persisted.refreshToken !== "string") {
    return null;
  }

  const accessToken = normalizeToken(persisted.accessToken);
  const refreshToken = normalizeToken(persisted.refreshToken);
  if (accessToken === null || refreshToken === null) {
    return null;
  }

  return {
    accessToken,
    refreshToken,
    persistence: mode,
  };
}

function readStoredSnapshot(): AuthSessionSnapshot | null {
  const persistent = readPersistedSnapshot("persistent");
  if (persistent !== null) {
    return persistent;
  }
  return readPersistedSnapshot("session");
}

function removePersistedSnapshot(mode: AuthPersistenceMode): void {
  const storage = readStorage(mode);
  if (storage === null) {
    return;
  }

  try {
    storage.removeItem(AUTH_STORAGE_KEY);
  } catch {
    // Ignore storage cleanup failures (private mode/quota).
  }
}

function clearPersistedSnapshots(): void {
  removePersistedSnapshot("persistent");
  removePersistedSnapshot("session");
}

function writePersistedSnapshot(snapshot: AuthSessionSnapshot): void {
  if (snapshot.accessToken === null || snapshot.refreshToken === null || snapshot.persistence === null) {
    clearPersistedSnapshots();
    return;
  }

  const inactiveMode: AuthPersistenceMode = snapshot.persistence === "persistent" ? "session" : "persistent";
  removePersistedSnapshot(inactiveMode);

  const storage = readStorage(snapshot.persistence);
  if (storage === null) {
    return;
  }
  try {
    storage.setItem(
      AUTH_STORAGE_KEY,
      JSON.stringify({
        accessToken: snapshot.accessToken,
        refreshToken: snapshot.refreshToken,
      }),
    );
  } catch {
    // Ignore storage write failures (private mode/quota).
  }
}

function resolveInitialSnapshot(initial?: Partial<AuthSessionSnapshot>): AuthSessionSnapshot {
  if (initial?.accessToken !== undefined || initial?.refreshToken !== undefined) {
    const accessToken =
      typeof initial?.accessToken === "string" ? normalizeToken(initial.accessToken) : initial?.accessToken ?? null;
    const refreshToken =
      typeof initial?.refreshToken === "string" ? normalizeToken(initial.refreshToken) : initial?.refreshToken ?? null;
    const persistence =
      accessToken !== null && refreshToken !== null
        ? isPersistenceMode(initial.persistence)
          ? initial.persistence
          : "persistent"
        : null;
    return {
      accessToken,
      refreshToken,
      persistence,
    };
  }

  const persisted = readStoredSnapshot();
  if (persisted !== null) {
    return persisted;
  }

  return {
    accessToken: null,
    refreshToken: null,
    persistence: null,
  };
}

class InMemoryAuthStore implements AuthStore {
  private snapshot: AuthSessionSnapshot;
  private listeners: Set<AuthStoreListener>;

  constructor(initial?: Partial<AuthSessionSnapshot>) {
    this.snapshot = resolveInitialSnapshot(initial);
    this.listeners = new Set<AuthStoreListener>();
  }

  getSnapshot(): AuthSessionSnapshot {
    return cloneSnapshot(this.snapshot);
  }

  getAccessToken(): string | null {
    return this.snapshot.accessToken;
  }

  getRefreshToken(): string | null {
    return this.snapshot.refreshToken;
  }

  getPersistenceMode(): AuthPersistenceMode | null {
    return this.snapshot.persistence;
  }

  hasSession(): boolean {
    return this.snapshot.accessToken !== null && this.snapshot.refreshToken !== null;
  }

  setTokens(tokens: SessionTokens, options?: SetSessionTokensOptions): AuthSessionSnapshot {
    const accessToken = normalizeToken(tokens.accessToken);
    const refreshToken = normalizeToken(tokens.refreshToken);

    if (accessToken === null || refreshToken === null) {
      return this.clear();
    }

    this.snapshot = {
      accessToken,
      refreshToken,
      persistence: options?.persistence ?? this.snapshot.persistence ?? "persistent",
    };

    writePersistedSnapshot(this.snapshot);
    this.notify();
    return this.getSnapshot();
  }

  clear(): AuthSessionSnapshot {
    this.snapshot = {
      accessToken: null,
      refreshToken: null,
      persistence: null,
    };

    writePersistedSnapshot(this.snapshot);
    this.notify();
    return this.getSnapshot();
  }

  subscribe(listener: AuthStoreListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notify(): void {
    const next = this.getSnapshot();
    for (const listener of this.listeners) {
      listener(next);
    }
  }
}

export function createAuthStore(initial?: Partial<AuthSessionSnapshot>): AuthStore {
  return new InMemoryAuthStore(initial);
}

export const authStore = createAuthStore();
